import React from 'react';

/**
 * The banner over each sport tab: a colour of its own and a piece of line art
 * drawn here, in SVG, so it costs no request and belongs to nobody.
 *
 * The art is deliberately schematic rather than photographic - a pitch, a
 * gridiron, the cage, a court - so it reads at a glance and sits quietly
 * behind the scores instead of competing with them.
 */

export const SPORT_THEME = {
  soccer: { hue: 145, name: 'Soccer', tagline: 'Every league that matters, kick-off to full time' },
  football: { hue: 28, name: 'Football', tagline: 'NFL and college, every snap of the season' },
  ufc: { hue: 4, name: 'Fight', tagline: 'UFC and boxing, prelims to the main event' },
  basketball: { hue: 22, name: 'Basketball', tagline: 'NBA, WNBA and college, tip-off to the buzzer' },
  all: { hue: 200, name: 'All sports', tagline: 'Everything on now, and starting soon, in every sport' },
  hockey: { hue: 195, name: 'Hockey', tagline: 'NHL and college, puck drop to the final horn' },
  baseball: { hue: 210, name: 'Baseball', tagline: 'MLB and college, first pitch to the last out' },
  tennis: { hue: 80, name: 'Tennis', tagline: 'ATP and WTA, wherever there is a stream' },
  motor: { hue: 0, name: 'Motor', tagline: 'F1, MotoGP, NASCAR and the rest, lights out to the flag' },
  rugby: { hue: 160, name: 'Rugby', tagline: 'Union and league, kick-off to the final whistle' },
  cricket: { hue: 100, name: 'Cricket', tagline: 'Tests, one-dayers and T20' },
  afl: { hue: 40, name: 'AFL', tagline: 'Aussie rules, first bounce to the siren' },
  golf: { hue: 120, name: 'Golf', tagline: 'The tours, tee to green' },
  darts: { hue: 350, name: 'Darts', tagline: 'PDC and the rest, game on' },
  other: { hue: 260, name: 'Other', tagline: 'Everything else with a stream behind it' }
};

// A broadcast mark for the sports without a drawing of their own
const Signal = () => (
  <svg viewBox="0 0 400 140" className="hero-art" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="300" cy="70" r="6" fill="currentColor" fillOpacity="0.5" />
      <path d="M282 52 A 26 26 0 0 0 282 88 M318 52 A 26 26 0 0 1 318 88" />
      <path d="M266 36 A 48 48 0 0 0 266 104 M334 36 A 48 48 0 0 1 334 104" />
      <path d="M250 20 A 70 70 0 0 0 250 120 M350 20 A 70 70 0 0 1 350 120" />
      <rect x="20" y="40" width="150" height="60" rx="3" />
      <line x1="95" y1="40" x2="95" y2="100" />
      <line x1="20" y1="70" x2="170" y2="70" strokeOpacity="0.5" />
    </g>
  </svg>
);

const Pitch = () => (
  <svg viewBox="0 0 400 140" className="hero-art" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="20" y="10" width="360" height="120" rx="2" />
      <line x1="200" y1="10" x2="200" y2="130" />
      <circle cx="200" cy="70" r="24" />
      <rect x="20" y="35" width="52" height="70" />
      <rect x="328" y="35" width="52" height="70" />
      <rect x="20" y="52" width="18" height="36" />
      <rect x="362" y="52" width="18" height="36" />
      <path d="M72 50 A 24 24 0 0 1 72 90" />
      <path d="M328 50 A 24 24 0 0 0 328 90" />
    </g>
    <g fill="none" stroke="currentColor" strokeWidth="1.6" transform="translate(300 40)">
      <circle r="26" />
      <path d="M0 -26 L0 -12 M-24.7 -8 L-11.4 -3.7 M-15.3 21 L-7 9.6 M15.3 21 L7 9.6 M24.7 -8 L11.4 -3.7" />
      <path d="M0 -12 L11.4 -3.7 L7 9.6 L-7 9.6 L-11.4 -3.7 Z" fill="currentColor" fillOpacity="0.5" />
    </g>
  </svg>
);

const Gridiron = () => (
  <svg viewBox="0 0 400 140" className="hero-art" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="20" y="10" width="360" height="120" />
      {[56, 92, 128, 164, 200, 236, 272, 308, 344].map((x) => (
        <line key={x} x1={x} y1="10" x2={x} y2="130" strokeWidth={x === 200 ? 2 : 1} />
      ))}
      <line x1="20" y1="50" x2="380" y2="50" strokeDasharray="2 6" strokeWidth="1" />
      <line x1="20" y1="90" x2="380" y2="90" strokeDasharray="2 6" strokeWidth="1" />
    </g>
    <g transform="translate(300 70) rotate(-25)" fill="none" stroke="currentColor" strokeWidth="1.8">
      <ellipse rx="40" ry="22" fill="currentColor" fillOpacity="0.16" />
      <path d="M-12 -7 L12 -7 M-8 -11 L-8 -3 M-3 -11 L-3 -3 M3 -11 L3 -3 M8 -11 L8 -3" />
      <path d="M-34 -8 A 40 22 0 0 1 -34 8 M34 -8 A 40 22 0 0 0 34 8" />
    </g>
  </svg>
);

const Octagon = () => (
  <svg viewBox="0 0 400 140" className="hero-art" aria-hidden="true">
    <g transform="translate(300 70)" fill="none" stroke="currentColor" strokeWidth="1.8">
      <polygon points="0,-58 41,-41 58,0 41,41 0,58 -41,41 -58,0 -41,-41" fill="currentColor" fillOpacity="0.1" />
      <polygon points="0,-46 32.5,-32.5 46,0 32.5,32.5 0,46 -32.5,32.5 -46,0 -32.5,-32.5" strokeWidth="1" strokeOpacity="0.6" />
      {[0, 45, 90, 135].map((angle) => (
        <line key={angle} x1="0" y1="-58" x2="0" y2="58" transform={`rotate(${angle})`} strokeWidth="0.8" strokeOpacity="0.45" />
      ))}
    </g>
    <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.55">
      {[30, 60, 90, 120, 150, 180].map((x) => (
        <line key={x} x1={x} y1="20" x2={x - 12} y2="120" />
      ))}
      <line x1="18" y1="20" x2="190" y2="20" />
      <line x1="6" y1="120" x2="178" y2="120" />
    </g>
  </svg>
);

const Court = () => (
  <svg viewBox="0 0 400 140" className="hero-art" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="20" y="10" width="360" height="120" />
      <line x1="200" y1="10" x2="200" y2="130" />
      <circle cx="200" cy="70" r="20" />
      <rect x="20" y="42" width="60" height="56" />
      <rect x="320" y="42" width="60" height="56" />
      <path d="M20 22 Q 118 70 20 118" />
      <path d="M380 22 Q 282 70 380 118" />
      <circle cx="80" cy="70" r="18" />
      <circle cx="320" cy="70" r="18" />
    </g>
    <g transform="translate(300 70)" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle r="28" fill="currentColor" fillOpacity="0.16" />
      <path d="M-28 0 L28 0 M0 -28 L0 28" />
      <path d="M-20 -20 Q 0 0 -20 20 M20 -20 Q 0 0 20 20" />
    </g>
  </svg>
);

const ART = { soccer: Pitch, football: Gridiron, ufc: Octagon, basketball: Court };

const SportHero = ({ sport, live, next }) => {
  const theme = SPORT_THEME[sport] || SPORT_THEME.other;
  const Art = ART[sport] || Signal;
  return (
    <div className="sport-hero" style={{ '--hue': theme.hue }}>
      <div className="sport-hero-text">
        <div className="sport-hero-kicker">
          {live ? (
            <>
              <span className="live-dot" />
              {live} live now
            </>
          ) : next ? (
            `Next up ${next}`
          ) : (
            'Nothing scheduled'
          )}
        </div>
        <h1 className="sport-hero-title">{theme.name}</h1>
        <p className="sport-hero-tagline">{theme.tagline}</p>
      </div>
      <Art />
    </div>
  );
};

export default SportHero;
